/**
 * Centralized Timezone Configuration
 * Single source of truth for all timezone dropdowns across the app
 * 
 * IMPORTANT: All timezone dropdowns should import from this file
 * to ensure consistency across the application.
 * 
 * Currently active regions: USA, UAE/Middle East
 * To expand: Add timezones to TIMEZONES array and update ACTIVE_REGIONS
 */

// Active regions - only these show in dropdowns
const ACTIVE_REGIONS = ['usa', 'middle_east'];

// Full timezone list (only active regions are exported)
const ALL_TIMEZONES = [
  // =============================================================================
  // ACTIVE: United States
  // =============================================================================
  { value: 'America/New_York', label: 'Eastern Time (EST)', abbr: 'EST', region: 'usa' },
  { value: 'America/Chicago', label: 'Central Time (CST)', abbr: 'CST', region: 'usa' },
  { value: 'America/Denver', label: 'Mountain Time (MST)', abbr: 'MST', region: 'usa' },
  { value: 'America/Phoenix', label: 'Arizona (MST)', abbr: 'MST', region: 'usa' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST)', abbr: 'PST', region: 'usa' },
  { value: 'America/Anchorage', label: 'Alaska (AKST)', abbr: 'AKST', region: 'usa' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)', abbr: 'HST', region: 'usa' },
  
  // =============================================================================
  // ACTIVE: Gulf / Middle East (UAE)
  // =============================================================================
  { value: 'Asia/Dubai', label: 'Gulf Time - Dubai/UAE (GST)', abbr: 'GST', region: 'middle_east' },
  { value: 'Asia/Abu_Dhabi', label: 'Gulf Time - Abu Dhabi (GST)', abbr: 'GST', region: 'middle_east' },
  
  // =============================================================================
  // INACTIVE: Uncomment when expanding to these regions
  // =============================================================================
  
  // // Gulf / Middle East (Other)
  // { value: 'Asia/Muscat', label: 'Gulf Time - Muscat/Oman (GST)', abbr: 'GST', region: 'middle_east' },
  // { value: 'Asia/Qatar', label: 'Arabia Time - Qatar (AST)', abbr: 'AST', region: 'middle_east' },
  // { value: 'Asia/Kuwait', label: 'Arabia Time - Kuwait (AST)', abbr: 'AST', region: 'middle_east' },
  // { value: 'Asia/Bahrain', label: 'Arabia Time - Bahrain (AST)', abbr: 'AST', region: 'middle_east' },
  // { value: 'Asia/Riyadh', label: 'Arabia Time - Saudi Arabia (AST)', abbr: 'AST', region: 'middle_east' },
  
  // // Europe
  // { value: 'Europe/London', label: 'London/UK (GMT)', abbr: 'GMT', region: 'europe' },
  // { value: 'Europe/Paris', label: 'Paris/France (CET)', abbr: 'CET', region: 'europe' },
  // { value: 'Europe/Berlin', label: 'Berlin/Germany (CET)', abbr: 'CET', region: 'europe' },
  
  // // Asia
  // { value: 'Asia/Singapore', label: 'Singapore (SGT)', abbr: 'SGT', region: 'asia' },
  // { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', abbr: 'HKT', region: 'asia' },
  // { value: 'Asia/Tokyo', label: 'Tokyo/Japan (JST)', abbr: 'JST', region: 'asia' },
  
  // // Australia / Pacific
  // { value: 'Australia/Sydney', label: 'Sydney (AEST)', abbr: 'AEST', region: 'oceania' },
  // { value: 'Pacific/Auckland', label: 'Auckland/NZ (NZST)', abbr: 'NZST', region: 'oceania' },
];

// Export only active timezones
export const TIMEZONES = ALL_TIMEZONES.filter(tz => ACTIVE_REGIONS.includes(tz.region));

// Grouped timezones for dropdown menus with sections
export const TIMEZONES_BY_REGION = {
  'United States': TIMEZONES.filter(tz => tz.region === 'usa'),
  'UAE / Gulf': TIMEZONES.filter(tz => tz.region === 'middle_east'),
};

// Country-specific timezone subsets
export const COUNTRY_TIMEZONES = {
  USA: TIMEZONES.filter(tz => tz.region === 'usa'),
  UAE: TIMEZONES.filter(tz => tz.region === 'middle_east'),
};

/**
 * Get timezone abbreviation from IANA timezone value
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Dubai')
 * @returns {string} Abbreviation (e.g., 'GST')
 */
export const getTimezoneAbbr = (timezone) => {
  const tz = ALL_TIMEZONES.find(t => t.value === timezone);
  return tz?.abbr || timezone?.split('/').pop() || 'UTC';
};

/**
 * Get timezone label from IANA timezone value
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Dubai')
 * @returns {string} Label (e.g., 'Gulf Time - Dubai/UAE (GST)')
 */
export const getTimezoneLabel = (timezone) => {
  const tz = ALL_TIMEZONES.find(t => t.value === timezone);
  return tz?.label || timezone;
};

/**
 * Get timezones for a specific country
 * @param {string} countryCode - Country code (e.g., 'UAE', 'USA')
 * @returns {Array} List of timezone objects for the country
 */
export const getTimezonesForCountry = (countryCode) => {
  return COUNTRY_TIMEZONES[countryCode] || TIMEZONES;
};

/**
 * Default timezone for UAE (primary market)
 */
export const DEFAULT_TIMEZONE = 'Asia/Dubai';

/**
 * Default timezone for USA
 */
export const DEFAULT_TIMEZONE_USA = 'America/Chicago';
